/**
 * Monthly-checks sync façade — mirrors the legacy
 * `patrimony_monthly_checks_v1` (personal) and
 * `patrimony_family_monthly_checks_v1` (family) localStorage keys against
 * the new `/patrimony/monthly-checks` backend endpoints.
 *
 * Same contract as the account-pending sync: legacy reads stay synchronous
 * (the existing RealEstatePage / FamilyRealEstatePage code keeps reading
 * localStorage), while writes are mirrored to the backend on every toggle.
 *
 * Per-toggle sync (no debounce) — toggles are user-paced, so the extra
 * round-trip latency is invisible. Failures are logged but don't roll back
 * the local UI state.
 */

import { monthlyChecksApi } from './api';

const KEYS = {
  personal: 'patrimony_monthly_checks_v1',
  family:   'patrimony_family_monthly_checks_v1',
};
const MIGRATION_FLAGS = {
  personal: 'migration_status_patrimony_monthly_checks_v1',
  family:   'migration_status_patrimony_family_monthly_checks_v1',
};

const _readLS = (scope) => {
  try { return JSON.parse(localStorage.getItem(KEYS[scope]) || '{}'); }
  catch { return {}; }
};
const _writeLS = (scope, data) => {
  try { localStorage.setItem(KEYS[scope], JSON.stringify(data || {})); } catch {}
};

const _setMigStatus = (scope, status, extra = {}) => {
  try {
    localStorage.setItem(MIGRATION_FLAGS[scope], JSON.stringify({
      ts: Date.now(), status, ...extra,
    }));
    // eslint-disable-next-line no-console
    console.info(`[monthlyChecksSync:${scope}] migration status: ${status}`, extra);
  } catch {}
};

const _migrationDone = (scope) => {
  try {
    const s = localStorage.getItem(MIGRATION_FLAGS[scope]);
    if (!s) return false;
    return JSON.parse(s).status === 'success';
  } catch { return false; }
};

const _flatToNested = (rows) => {
  const nested = {};
  for (const r of rows || []) {
    if (!nested[r.month]) nested[r.month] = {};
    nested[r.month][r.asset_id] = true;
  }
  return nested;
};

const _hydrateScope = async (scope) => {
  // First-time migration: push local → backend then verify.
  if (!_migrationDone(scope)) {
    const local = _readLS(scope);
    const pairs = [];
    for (const [month, byAsset] of Object.entries(local || {})) {
      for (const [assetId, checked] of Object.entries(byAsset || {})) {
        if (checked) pairs.push({ asset_id: assetId, month, scope });
      }
    }
    if (pairs.length > 0) {
      try {
        // POST every pair (idempotent upsert).
        for (const p of pairs) {
          await monthlyChecksApi.set(p.asset_id, p.month, scope);
        }
        // Verify
        const r = await monthlyChecksApi.list(scope);
        if (!Array.isArray(r.data) || r.data.length < pairs.length) {
          _setMigStatus(scope, 'error', { reason: 'verification_short',
                                          expected: pairs.length,
                                          got: r.data?.length });
          return;
        }
        _setMigStatus(scope, 'success', { migrated: pairs.length });
      } catch (err) {
        _setMigStatus(scope, 'error', { error: String(err?.message || err) });
        return; // KEEP localStorage, retry next mount
      }
    } else {
      _setMigStatus(scope, 'success', { migrated: 0, note: 'no_local_data' });
    }
  }

  // Authoritative pull.
  try {
    const r = await monthlyChecksApi.list(scope);
    _writeLS(scope, _flatToNested(r.data));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[monthlyChecksSync:${scope}] hydrate pull failed`, err);
  }
};

export const hydrateMonthlyChecks = async () => {
  await Promise.all([_hydrateScope('personal'), _hydrateScope('family')]);
};

/**
 * Set or unset a check, mirroring the change to backend.
 * Updates localStorage synchronously so the UI sees the change immediately;
 * the backend POST/DELETE happens in the background.
 */
export const toggleMonthlyCheck = (scope, assetId, month, value) => {
  // Local state first — synchronous UI feedback
  const data = _readLS(scope);
  if (value) {
    if (!data[month]) data[month] = {};
    data[month][assetId] = true;
  } else if (data[month]) {
    delete data[month][assetId];
  }
  _writeLS(scope, data);

  // Backend mirror — fire and forget; errors are logged but don't roll back
  // the UI (the value will reconcile on next hydrate).
  if (value) {
    monthlyChecksApi.set(assetId, month, scope).catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`[monthlyChecksSync:${scope}] set failed`, err);
    });
  } else {
    monthlyChecksApi.remove(assetId, month, scope).catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`[monthlyChecksSync:${scope}] remove failed`, err);
    });
  }
};
